import type { EntityAttachment } from "@/types/entity-attachment";

export const ListType = {
  DIRECT: "DIRECT",
  INTEREST: "INTEREST",
} as const;
export type ListType = (typeof ListType)[keyof typeof ListType];

export const PaymentType = {
  INSTANT: "INSTANT",
  SCHEDULED: "SCHEDULED",
} as const;
export type PaymentType = (typeof PaymentType)[keyof typeof PaymentType];

export const GameStatus = {
  OPEN: "OPEN",
  CLOSED: "CLOSED",
  CANCELLED: "CANCELLED",
  COMPLETED: "COMPLETED",
} as const;
export type GameStatus = (typeof GameStatus)[keyof typeof GameStatus];

export interface Game {
  id: string;
  /** Código curto para link de compartilhamento (ex: /g/abc12xyz) */
  shortCode?: string;
  name: string;
  sport?: string;
  /** Código de categoria opcional dentro do esporte (ex. RUN_5K). */
  sportCategory?: string | null;
  location: string;
  locationLat?: number;
  locationLng?: number;
  locationRadius?: number;
  gameDate: string; // ISO 8601
  gameEndDate?: string; // ISO 8601
  maxParticipants: number;
  listType: ListType;
  listOpenAt?: string; // ISO 8601
  listCloseAt?: string; // ISO 8601
  interestStartAt?: string; // ISO 8601
  interestEndAt?: string; // ISO 8601
  listRevealAt?: string; // ISO 8601
  allowWaitlist: boolean;
  paymentTimeoutMinutes: number;
  allowRefund: boolean;
  refundDeadlineHours: number;
  pricePerPlayer: number;
  estimatedCost?: number;
  paymentType?: PaymentType;
  paymentDeadline?: string; // ISO 8601
  userId: string;
  /** ID do organizador (pode ser diferente do criador). Se ausente, o criador é o organizador. */
  organizerUserId?: string | null;
  teamId: string;
  status: GameStatus;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  /** Se true, o usuário paga preço + taxa; se false, time absorve a taxa. */
  passFeesToUser: boolean;
  /** Se false, administradores do time pagam o próprio ingresso como qualquer membro. */
  allowAdminDiscount: boolean;
  team?: {
    id: string;
    slug?: string;
    name: string;
    photo?: string;
    /** Dono do time (para permissões no cliente, ex.: sortear equipes) */
    userId?: string;
  };
  /** Número de participantes confirmados (apenas em listagens públicas) */
  confirmedCount?: number;
  /** Meus jogos enriquecido: permissão de gestão */
  canManageGame?: boolean;
  /** Meus jogos enriquecido: resumo de avaliações pós-jogo (colegas de equipe) */
  myTeammateRatingsSummary?: {
    teammatesToRate: number;
    ratingsGiven: number;
    ratingsReceivedCount: number;
    averageReceivedStars: number | null;
  } | null;
  /** URL da imagem de capa do jogo (card) */
  coverImage?: string;
  attachments?: EntityAttachment[];
  viplayArenaId?: string | null;
  viplayQuadraIds?: string[];
  viplayAutoSync?: boolean;
  viplayLastSyncedAt?: string | null;
}

export interface CreateGameData {
  name: string;
  sport?: string;
  location: string;
  locationLat?: number;
  locationLng?: number;
  locationRadius?: number;
  gameDate: string; // ISO 8601
  gameEndDate?: string; // ISO 8601
  maxParticipants: number;
  listType: ListType;
  listOpenAt?: string; // ISO 8601
  listCloseAt?: string; // ISO 8601
  interestStartAt?: string; // ISO 8601
  interestEndAt?: string; // ISO 8601
  listRevealAt?: string; // ISO 8601
  allowWaitlist?: boolean;
  paymentTimeoutMinutes?: number;
  allowRefund?: boolean;
  refundDeadlineHours?: number;
  pricePerPlayer: number;
  estimatedCost?: number;
  paymentType?: PaymentType;
  paymentDeadline?: string; // ISO 8601
  teamId: string;
  sportCategory?: string | null;
  passFeesToUser?: boolean;
  /** default true no servidor se omitido */
  allowAdminDiscount?: boolean;
  /** Opcional: ID do usuário organizador (OWNER ou ADMIN do time). */
  organizerUserId?: string;
  viplayArenaId?: string;
  viplayQuadraIds?: string[];
  viplayAutoSync?: boolean;
}

export interface UpdateGameData extends Partial<CreateGameData> {
  status?: GameStatus;
}

export interface GetGamesFilter {
  sport?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  /** true: com datas, API inclui jogos já encerrados (CLOSED/COMPLETED) */
  includeFinishedStatuses?: string;
  search?: string;
  latitude?: string;
  longitude?: string;
  distance?: string;
}

export interface MyParticipationsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export type EnrichedParticipatedGame = Game & {
  confirmedCount: number;
  userParticipantStatus: string;
  verificationCode: string | null;
  paymentExpiresAt: string | null;
  checkedInAt: string | null;
  canManageGame: boolean;
  myTeammateRatingsSummary: Game["myTeammateRatingsSummary"];
};

export interface MyParticipationsEnrichedResponse {
  games: EnrichedParticipatedGame[];
  pagination?: MyParticipationsPagination;
}

export interface FindUserParticipatedGamesEnrichedParams {
  scope?: "all" | "upcoming" | "history";
  page?: number;
  limit?: number;
}
